<template>
  <v-card outlined class="py-3 px-5 w-100">
    <v-row>
      <!-- LEFT rail -->
      <v-col
        v-if="leftRailGroups.length"
        cols="12"
        md="3"
        xl="3"
        class="pb-4 d-flex"
      >
        <div class="d-flex flex-column w-100">
          <div v-for="g in leftRailGroups" :key="`L-${g}`" class="mb-2">
            <v-card outlined class="py-3 px-5 card-flex">
              <div class="card-body-flex">
                <table class="w-100 table-stretch">
                  <thead>
                    <tr>
                      <th class="table-title group" colspan="2">
                        {{ $t(g) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(b, i) in sections(g)">
                      <tr :key="`L-${g}-sec-${i}`">
                        <th class="table-title" colspan="2">
                          {{ b.sectionTitle }}
                        </th>
                      </tr>
                      <tr v-for="r in b.rows" :key="`L-${g}-row-${r.rowKey}`">
                        <td>{{ r.title }}</td>
                        <td>
                          {{ r.display }}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </v-card>
          </div>
        </div>
      </v-col>

      <!-- MIDDLE rail -->
      <v-col
        v-if="midRailGroups.length"
        cols="12"
        md="3"
        xl="3"
        class="pb-4 d-flex"
      >
        <div class="d-flex flex-column w-100">
          <div
            v-for="(g, i) in midRailGroups"
            :key="`M-${g}`"
            class="mb-2"
            :class="{ 'flex-grow-1 ': i === midRailGroups.length - 1 }"
          >
            <v-card
              outlined
              class="py-3 px-5 card-flex"
              :class="{ 'h-100': i === midRailGroups.length - 1 }"
            >
              <div class="card-body-flex">
                <table class="w-100 table-stretch">
                  <thead>
                    <tr>
                      <th class="table-title group" colspan="2">
                        {{ $t(g) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(b, j) in sections(g)">
                      <tr :key="`M-${g}-sec-${j}`">
                        <th class="table-title" colspan="2">
                          {{ b.sectionTitle }}
                        </th>
                      </tr>
                      <tr v-for="r in b.rows" :key="`M-${g}-row-${r.rowKey}`">
                        <td>{{ r.title }}</td>
                        <td>
                          {{ r.display }}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </v-card>
          </div>
        </div>
      </v-col>

      <!-- RIGHT rail  -->
      <v-col cols="12" md="6" xl="6" class="pb-4 d-flex">
        <v-row class="w-100">
          <v-col cols="12" class="mb-2">
            <v-card outlined class="py-3 px-5 card-flex h-100">
              <div class="card-body-flex">
                <table class="w-100 table-stretch">
                  <thead>
                    <tr>
                      <th class="table-title group" colspan="8">
                        {{ $t("process_flows") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(b, i) in sections('process_flows')">
                      <tr :key="`PF-sec-${i}`">
                        <th class="table-title" colspan="8">
                          {{ b.sectionTitle }}
                        </th>
                      </tr>
                      <tr
                        v-for="(quad, j) in fourColumnify(b.rows)"
                        :key="`PF-row-${i}-${j}`"
                      >
                        <template v-for="(c, k) in quad">
                          <td :key="`PF-cell-${i}-${j}-${k}-label`">
                            {{ c ? c.title : "" }}
                          </td>
                          <td :key="`PF-cell-${i}-${j}-${k}-value`">
                            {{ c ? c.display : "" }}
                          </td>
                        </template>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </v-card>
          </v-col>
          <v-col
            v-for="g in rightRailNonPF"
            :key="`R-${g}`"
            cols="12"
            md="6"
            xl="6"
            class="pb-4 d-flex"
          >
            <v-card outlined class="py-3 px-5 card-flex h-100 w-100">
              <div class="card-body-flex">
                <table class="w-100 table-stretch">
                  <thead>
                    <tr>
                      <th class="table-title group" colspan="2">
                        {{ $t(g) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(b, i) in sections(g)">
                      <tr :key="`R-${g}-sec-${i}`">
                        <th class="table-title" colspan="2">
                          {{ b.sectionTitle }}
                        </th>
                      </tr>
                      <tr v-for="r in b.rows" :key="`R-${g}-row-${r.rowKey}`">
                        <td>{{ r.title }}</td>
                        <td>
                          {{ r.display }}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { BALANCE_FIELD_GROUPS } from "@/views/balance/data";

export default {
  name: "BalanceHistoryInputCard",

  props: {
    record: { type: Object, required: true },
  },

  computed: {
    orderedGroups() {
      const avail = [
        "natural_gas",
        "air_flows",
        "process_flows",
        "pellet_sponge",
        "thermal_recovery",
      ];
      const hasPF = (function () {
        for (let i = 0; i < avail.length; i++) {
          if (avail[i] === "process_flows") return true;
        }
        return false;
      })();
      const nonPF = (function () {
        const arr = [];
        for (let i = 0; i < avail.length; i++) {
          const g = avail[i];
          if (g !== "process_flows") arr.push(g);
        }
        return arr;
      })();
      const leftTwo = nonPF.slice(0, 2);
      const afterLeft = nonPF.slice(2);
      const rightTwo = afterLeft.slice(Math.max(0, afterLeft.length - 2));
      const middlePool = afterLeft.slice(0, afterLeft.length - rightTwo.length);
      const out = [
        ...leftTwo,
        ...(hasPF ? ["process_flows"] : []),
        ...middlePool,
        ...rightTwo,
      ];
      if (out.length >= 4) {
        const tmp = out[1];
        out[1] = out[3];
        out[3] = tmp;
      }
      return out;
    },
    leftRailGroups() {
      const n = this.orderedGroups.length;
      return this.orderedGroups.slice(0, Math.min(2, n));
    },
    midRailGroups() {
      const n = this.orderedGroups.length;
      return this.orderedGroups.slice(Math.max(0, n - 2));
    },
    rightRailGroups() {
      const n = this.orderedGroups.length;
      const start = Math.min(2, n);
      const end = Math.max(start, n - 2);
      return this.orderedGroups.slice(start, end);
    },
    rightRailNonPF() {
      const src = this.rightRailGroups;
      const out = [];
      for (let i = 0; i < src.length; i++) {
        if (src[i] !== "process_flows") out.push(src[i]);
      }
      return out;
    },
    defsByGroup() {
      const out = {};
      const groups = BALANCE_FIELD_GROUPS || {};
      for (const group in groups) {
        const sections = groups[group] || [];
        const arr = [];
        for (let idx = 0; idx < sections.length; idx++) {
          const sec = sections[idx] || {};
          const fields = sec.fields || [];
          for (let fIdx = 0; fIdx < fields.length; fIdx++) {
            const f = fields[fIdx] || {};
            arr.push({
              key: f.key,
              title: f.title,
              sectionTitle: sec.title,
              sectionIndex: idx,
            });
          }
        }
        out[group] = arr;
      }
      return out;
    },
    sectionOrder() {
      const order = {};
      const groups = BALANCE_FIELD_GROUPS || {};
      for (const group in groups) {
        const sections = groups[group] || [];
        const titles = [];
        for (let i = 0; i < sections.length; i++) {
          titles.push(sections[i] && sections[i].title);
        }
        order[group] = titles;
      }
      return order;
    },
  },

  methods: {
    catGroup(g) {
      return g === "pellet_sponge" ? "ps_irons" : g;
    },
    viewOf(g) {
      if (!this.record) return {};
      const ng = this.record.natural_gas || {};
      const pf = this.record.process_flows || {};
      if (g === "process_flows") {
        const cdbus = {};
        for (const k in ng) {
          if (k.indexOf("cdbus_") === 0) cdbus[k] = ng[k];
        }
        const merged = {};
        for (const k in pf) merged[k] = pf[k];
        for (const k in cdbus) merged[k] = cdbus[k];
        return merged;
      }
      if (g === "natural_gas") {
        const clean = {};
        for (const k in ng) {
          if (k.indexOf("cdbus_") !== 0) clean[k] = ng[k];
        }
        return clean;
      }
      return this.record[g] || {};
    },
    rows(g) {
      const obj = this.viewOf(g);
      let hasAny = false;
      const objKeys = [];
      for (const k in obj) {
        hasAny = true;
        objKeys.push(k);
      }
      if (!hasAny) return [];
      const cg = this.catGroup(g);
      const defs = this.defsByGroup[cg] || [];
      const defPairLookup = new Set();
      const defOrderedPairs = [];
      for (let i = 0; i < defs.length; i++) {
        const d = defs[i];
        const pairId = `${d.key}__s${d.sectionIndex}`;
        if (!defPairLookup.has(pairId)) {
          defPairLookup.add(pairId);
          defOrderedPairs.push({ key: d.key, sectionIndex: d.sectionIndex });
        }
      }
      const defKeyOnlyLookup = new Set(defs.map((d) => d.key));
      for (let i = 0; i < objKeys.length; i++) {
        const k = objKeys[i];
        if (!defKeyOnlyLookup.has(k)) {
          defOrderedPairs.push({ key: k, sectionIndex: null });
        }
      }
      const out = [];
      for (let i = 0; i < defOrderedPairs.length; i++) {
        const { key, sectionIndex } = defOrderedPairs[i];
        let def = null;
        if (typeof sectionIndex === "number") {
          for (let j = 0; j < defs.length; j++) {
            const d = defs[j];
            if (d.key === key && d.sectionIndex === sectionIndex) {
              def = d;
              break;
            }
          }
        } else {
          for (let j = 0; j < defs.length; j++) {
            if (defs[j].key === key) {
              def = defs[j];
              break;
            }
          }
        }
        const rowKey =
          typeof sectionIndex === "number" ? `${key}__s${sectionIndex}` : key;
        out.push({
          key,
          rowKey,
          title: def.title,
          sectionTitle: def.sectionTitle,
          display: String(obj[key]),
        });
      }
      return out;
    },
    sections(g) {
      const r = this.rows(g);
      if (!r.length) return [];
      const cg = this.catGroup(g);
      const order = this.sectionOrder[cg] || [];
      const buckets = {};
      for (let i = 0; i < r.length; i++) {
        const x = r[i];
        const t = x.sectionTitle;
        if (!buckets[t]) buckets[t] = [];
        buckets[t].push(x);
      }
      const out = [];
      for (let i = 0; i < order.length; i++) {
        const t = order[i];
        if (buckets[t] && buckets[t].length) {
          out.push({ sectionTitle: t, rows: buckets[t] });
          delete buckets[t];
        }
      }
      for (const t in buckets) {
        out.push({ sectionTitle: t, rows: buckets[t] });
      }
      return out;
    },
    fourColumnify(list) {
      const out = [];
      for (let i = 0; i < list.length; i += 4) {
        const chunk = list.slice(i, i + 4);
        while (chunk.length < 4) chunk.push(null);
        out.push(chunk);
      }
      return out;
    },
  },
};
</script>

<style scoped>
.table-title {
  font-weight: 600;
  padding: 6px 4px;
}
.group {
  font-size: 14px;
  background-color: rgb(243, 243, 243);
}
.card-flex {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.card-body-flex {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.table-stretch {
  flex: 1 1 auto;
}
</style>
